import API, { apiErrorMessage } from "../global/api";
import {
  addingInvoiceForMeCall,
  deletingInvoiceForMeCall,
  addingBulkInvoiceForMeCall,
} from "./HeaderAction";

// Add Invoices
export const addInvoicesRequest = () => {
  return {
    type: "ADD_INVOICES_REQUEST",
  };
};
export const addInvoicesSuccess = (data) => {
  return {
    type: "ADD_INVOICES_SUCCESS",
    payload: data,
  };
};
export const addInvoicesError = (msg) => {
  return {
    type: "ADD_INVOICES_ERROR",
    payload: msg,
  };
};
// What to tell the user when FBR's answer is neither Valid nor Invalid - or is
// text this screen cannot read. The invoice is already saved by then.
const unreadFbrAnswerText = (fbrResponse) => {
  const detail =
    fbrResponse?.validationResponse?.error ||
    (typeof fbrResponse === "string" ? fbrResponse : "");
  return `The invoice was saved, but FBR's answer could not be read, so it is not confirmed as posted. Check its FBR status in the invoice list before posting it again.${
    detail ? ` FBR said: ${detail}` : ""
  }`;
};

// The text for a failed save, whatever shape the failure took: the server's
// message (a string, or FBR's list of per-item errors), a server error with no
// message, no answer at all, or a fault in this code.
const saveErrorText = (error) => {
  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  if (Array.isArray(message)) {
    const lines = message
      .map((entry) =>
        typeof entry === "string" ? entry : entry?.error || entry?.message,
      )
      .filter(Boolean);
    if (lines.length) return lines.join(" ");
  }
  // axios 0.27 hands a request that got no answer (offline, timed out, blocked
  // by the browser) back with the request itself as `response`, status 0.
  if (error?.response && error.response.status) {
    // A gateway's error page: the server may well have finished the save
    // behind it. The form's draft key makes saving again safe.
    if ([502, 503, 504].includes(error.response.status)) {
      return `The server did not finish answering (error ${error.response.status}), so it is not known whether the invoice was saved. Check the invoice list - saving this form again will not file it twice.`;
    }
    return `The server could not save the invoice (error ${error.response.status}). Please try again.`;
  }
  if (error?.request || error?.response) {
    return "The server did not answer, so it is not known whether the invoice was saved. Check the invoice list - saving this form again will not file it twice.";
  }
  return error?.message || "The invoice could not be saved. Please try again.";
};

// The empty form a save leaves behind.
const blankInvoice = () => ({
  transactionType: "",
  invoiceNumber: "",
  invoiceType: "Sale Invoice",
  date: "",
  purchaseOrderNo: "",
  salesTerm: "",
  section236G: 0,
  GSection236H: 0,
  FBRInvoiceRefNo: "",
  debitNoteReason: "",
  saveInvoiceStatus: 1,
  companyId: "",
  buyerId: "",
  scenarioId: "",
  billNo: "",
  userId: "",
  items: [
    {
      itemId: "",
      hsCode: "",
      productDescription: "",
      rateValue: "",
      rate: "",
      uoM: "",
      quantity: 1,
      totalValues: 0,
      valueSalesExcludingST: "",
      unitPrice: 0,
      salesTaxApplicable: 0,
      salesTaxWithheldAtSource: 0,
      extraTax: "",
      furtherTax: 0,
      sroScheduleNo: 0,
      fedPayable: 0,
      discount: 0,
      saleType: "",
      sroItemSerialNo: 0,
      fixedNotifiedValueOrRetailPrice: "",
    },
  ],
  subTotal: 0,
  taxTotal: 0,
  grandTotal: 0,
});

// `idempotencyKey` is the form's draft key (global/draftKey.js), sent with
// every Save of the draft; `onSaved` lets the form start a new draft once this
// one is saved. The key travels in the body, not as a header: a browser sends
// a custom header only if the server's CORS reply lists it, and a missing
// entry there blocked every save.
export const addInvoices = (
  data,
  setFormData,
  handleVisible,
  handleDangerVisible,
  { idempotencyKey, onSaved } = {},
) => {
  return (dispatch) => {
    dispatch(addInvoicesRequest());
    API.post(
      `/invoice/add-invoice`,
      idempotencyKey ? { ...data, idempotencyKey } : data,
    )
      .then((res) => {
        let data1 = res.data;
        dispatch(addInvoicesSuccess(data1));
        dispatch(addingInvoiceForMeCall());
        // Any answer that gets here means the invoice is saved, whatever FBR
        // made of it. The form is emptied and the next Save is a new draft:
        // pressing Save again on this one filed a second invoice with the
        // same number.
        if (onSaved) onSaved();

        if (data1?.fbrError) {
          // Saved, but FBR could not be reached or did not answer.
          handleDangerVisible(data1.message);
          setFormData(blankInvoice());
          return;
        }
        if (!data1?.fbrResponse) {
          handleVisible(
            "INVOICE SAVED SUCESSFULLY AND READY TO SEND TO FBR",
            "success",
            null,
            data1?.savedInvoice,
          );
          setFormData(blankInvoice());
        }
        if (data1?.fbrResponse?.validationResponse?.status === "Valid") {
          handleVisible(
            `FBR INVOICE# ${data1?.fbrResponse?.invoiceNumber}`,
            "success",
            data1?.fbrResponse?.validationResponse,
            data1?.savedInvoice,
          );
          setFormData({
            transactionType: "",
            invoiceNumber: "",
            invoiceType: "",
            date: "",
            purchaseOrderNo: "",
            salesTerm: "",
            section236G: 0,
            GSection236H: 0,
            FBRInvoiceRefNo: "",
            debitNoteReason: "",
            saveInvoiceStatus: 1,
            companyId: "",
            buyerId: "",
            scenarioId: "",
            billNo: "",
            userId: "",
            items: [
              {
                itemId: "",
                hsCode: "",
                productDescription: "",
                rateValue: "",
                rate: "",
                uoM: "",
                quantity: 1,
                totalValues: 0,
                valueSalesExcludingST: "",
                unitPrice: 0,
                salesTaxApplicable: 0,
                salesTaxWithheldAtSource: 0,
                extraTax: "",
                furtherTax: 0,
                sroScheduleNo: 0,
                fedPayable: 0,
                discount: 0,
                saleType: "",
                sroItemSerialNo: 0,
                fixedNotifiedValueOrRetailPrice: "",
              },
            ],
            subTotal: 0,
            taxTotal: 0,
            grandTotal: 0,
          });
        } else if (
          data1?.fbrResponse?.validationResponse?.status === "Invalid"
        ) {
          handleVisible(
            "",
            "error",
            data1?.fbrResponse?.validationResponse,
            data1?.savedInvoice,
          );
          // Saved with FBR's rejection; it is corrected from the invoice list.
          setFormData(blankInvoice());
        } else if (data1?.fbrResponse) {
          // Any other answer used to fall through every branch above, and the
          // screen showed nothing at all.
          handleDangerVisible(unreadFbrAnswerText(data1.fbrResponse));
          setFormData(blankInvoice());
        }
      })
      .catch((error) => {
        // Read defensively: a failure with no server answer (offline, timed
        // out) has no `response`, and reading through it threw here, so no
        // message was shown and the button stayed on "Please Wait...".
        const message = saveErrorText(error);
        dispatch(addInvoicesError(message));
        handleDangerVisible(message);
      });
  };
};

// Getting Get Invoices List
export const getInvoicesRequest = () => {
  return {
    type: "INVOICES_LIST_REQUEST",
  };
};
export const getInvoicesSuccess = (data) => {
  return {
    type: "INVOICES_LIST_SUCCESS",
    payload: data,
  };
};
export const getInvoicesError = (msg) => {
  return {
    type: "INVOICES_LIST_ERROR",
    payload: msg,
  };
};
// Only the newest list request may fill the list: typing in a search sends one
// request per pause, and an older answer arriving late must not replace the
// newer one.
let latestInvoiceListRequest = 0;

/**
 * `companyFilter` is the Invoice List's Company search, optional:
 *   companyId    one company, picked from the suggestions
 *   companyName  any part of a company name, as typed
 */
export const getInvoices = (
  page,
  limit,
  globalFilter,
  companyStatus,
  FBRInvoiceStatus,
  companyFilter = {},
) => {
  return (dispatch) => {
    const requestNo = ++latestInvoiceListRequest;
    dispatch(getInvoicesRequest());
    const companyId = companyFilter?.companyId
      ? `&companyId=${encodeURIComponent(companyFilter.companyId)}`
      : "";
    const companyName = String(companyFilter?.companyName ?? "").trim()
      ? `&companyName=${encodeURIComponent(companyFilter.companyName)}`
      : "";
    API.get(
      `/invoice/get-invoices?page=${page}&limit=${limit}&search=${globalFilter}&companyStatus=${companyStatus}&FBRInvoiceStatus=${FBRInvoiceStatus}${companyId}${companyName}`,
    )
      .then((res) => {
        if (requestNo !== latestInvoiceListRequest) return;
        let invoicesList = res.data;
        dispatch(getInvoicesSuccess(invoicesList));
      })
      .catch((error) => {
        if (requestNo !== latestInvoiceListRequest) return;
        dispatch(getInvoicesError(error?.response?.data?.message));
      });
  };
};

// Updating Hs Code
export const updateInvoicesRequest = () => {
  return {
    type: "UPDATE_INVOICES_LIST_REQUEST",
  };
};
export const updateInvoicesSuccess = (data) => {
  return {
    type: "UPDATE_INVOICES_LIST_SUCCESS",
    payload: data,
  };
};
export const updateInvoicesError = (msg) => {
  return {
    type: "UPDATE_INVOICES_LIST_ERROR",
    payload: msg,
  };
};
export const updateInvoices = (
  id,
  data,
  handleVisible,
  handleDangerVisible,
) => {
  return (dispatch) => {
    dispatch(updateInvoicesRequest());
    API.put(`/invoice/update-invoice/${id}`, data)
      .then((res) => {
        let updateInvoicesList = res.data;
        dispatch(updateInvoicesSuccess(updateInvoicesList));
        // if (!updateInvoicesList?.updatedInvoice?.FBRInvoiceStatusResponse) {
        handleVisible(
          "INVOICE SAVED SUCESSFULLY AND READY TO SEND TO FBR",
          "success",
          null,
        );
        // }
        // if (
        //   updateInvoicesList?.updatedInvoice?.FBRInvoiceStatusResponse
        //     ?.validationResponse?.status === "Valid"
        // ) {
        //   handleVisible(
        //     `FBR INVOICE# ${updateInvoicesList?.updatedInvoice?.FBRInvoiceStatusResponse?.invoiceNumber}`,
        //     "success",
        //     updateInvoicesList?.updatedInvoice?.FBRInvoiceStatusResponse
        //       ?.validationResponse,
        //   );
        // } else if (
        //   updateInvoicesList?.updatedInvoice?.FBRInvoiceStatusResponse
        //     ?.validationResponse?.status === "Invalid"
        // ) {
        //   handleVisible(
        //     "",
        //     "error",
        //     updateInvoicesList?.updatedInvoice?.FBRInvoiceStatusResponse
        //       ?.validationResponse,
        //   );
        // }
      })
      .catch((error) => {
        dispatch(updateInvoicesError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};

// Deleting Hs Code
export const deleteInvoicesRequest = () => {
  return {
    type: "DELETE_INVOICES_LIST_REQUEST",
  };
};
export const deleteInvoicesSuccess = (data) => {
  return {
    type: "DELETE_INVOICES_LIST_SUCCESS",
    payload: data,
  };
};
export const deleteInvoicesError = (msg) => {
  return {
    type: "DELETE_INVOICES_LIST_ERROR",
    payload: msg,
  };
};
export const deleteInvoices = (
  data,
  handleVisible,
  handleDangerVisible,
  type,
  setRowSelection,
  page,
  limit,
  searchTerm,
  companyStatus,
  FBRInvoiceStatus,
  companyFilter,
) => {
  return (dispatch) => {
    dispatch(deleteInvoicesRequest());
    API.post(`/invoice/delete-invoice`, data)
      .then((res) => {
        let deleteInvoicesList = res.data;
        dispatch(deleteInvoicesSuccess(deleteInvoicesList));
        dispatch(deletingInvoiceForMeCall(data?.invoiceIds?.length));
        if (type === "single") {
          handleVisible(deleteInvoicesList.results[0]?.message);
        } else {
          handleVisible(res.data.message);
          setRowSelection({});
        }
        dispatch(
          getInvoices(
            page,
            limit,
            searchTerm,
            companyStatus,
            FBRInvoiceStatus,
            companyFilter,
          ),
        );
      })
      .catch((error) => {
        dispatch(deleteInvoicesError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};

// Getting Transaction Type
export const getTransactionRequest = () => {
  return {
    type: "TRANSACTION_LIST_REQUEST",
  };
};
export const getTransactionSuccess = (data) => {
  return {
    type: "TRANSACTION_LIST_SUCCESS",
    payload: data,
  };
};
export const getTransactionError = (msg) => {
  return {
    type: "TRANSACTION_LIST_ERROR",
    payload: msg,
  };
};
export const getTransaction = () => {
  return (dispatch) => {
    dispatch(getTransactionRequest());
    API.get(`/scenario/get-scenarios?&limit=1000`)
      .then((res) => {
        let transactionType = res.data;
        dispatch(getTransactionSuccess(transactionType));
      })
      .catch((error) => {
        dispatch(getTransactionError(apiErrorMessage(error)));
      });
  };
};

// Getting Invoice Number
export const getInvoiceNumberRequest = () => {
  return {
    type: "INVOICE_NUMBER_LIST_REQUEST",
  };
};
export const getInvoiceNumberSuccess = (data) => {
  return {
    type: "INVOICE_NUMBER_LIST_SUCCESS",
    payload: data,
  };
};
export const getInvoiceNumberError = (msg) => {
  return {
    type: "INVOICE_NUMBER_LIST_ERROR",
    payload: msg,
  };
};
// `userId` is the user the invoice is being filed for. An admin files on
// behalf of someone else, so the running sequence belongs to that user - without
// it the server counts the admin's own invoices and the number restarts at 1.
export const getInvoiceNumber = (id, setFormData, companyShortName, userId) => {
  return (dispatch) => {
    dispatch(getInvoiceNumberRequest());
    API.get(`/invoice/get-last-invoice/${id}`, {
      params: userId ? { userId } : undefined,
    })
      .then((res) => {
        let invoiceNumber = res.data;
        dispatch(getInvoiceNumberSuccess(invoiceNumber));
        setFormData((prev) => ({
          ...prev,
          invoiceNumber: `${companyShortName}-${invoiceNumber.lastSequenceNumber}`,
        }));
      })
      .catch((error) => {
        dispatch(getInvoiceNumberError(apiErrorMessage(error)));
      });
  };
};

// Send to FBR
export const sendInvoiceToFbrRequest = () => {
  return {
    type: "SEND_INVOICE_TO_FBR_LIST_REQUEST",
  };
};
export const sendInvoiceToFbrSuccess = (data) => {
  return {
    type: "SEND_INVOICE_TO_FBR_LIST_SUCCESS",
    payload: data,
  };
};
export const sendInvoiceToFbrError = (msg) => {
  return {
    type: "SEND_INVOICE_TO_FBR_LIST_ERROR",
    payload: msg,
  };
};
export const sendInvoiceToFbr = (
  data,
  handleVisible,
  handleDangerVisible,
  type,
  setRowSelection,
  page,
  limit,
  searchTerm,
  companyStatus,
  FBRInvoiceStatus,
  companyFilter,
) => {
  return (dispatch) => {
    dispatch(sendInvoiceToFbrRequest());
    API.post(`/invoice/approve-invoice`, data)
      .then((res) => {
        let sendingInvoicesList = res.data;
        dispatch(sendInvoiceToFbrSuccess(sendingInvoicesList));
        if (type === "single") {
          const result = sendingInvoicesList?.results?.[0];
          const validationResponse = result?.fbrResponse?.validationResponse;
          // Only an explicit "Valid" verdict from FBR means the invoice is posted.
          // Anything else (Invalid, skipped, or a failed attempt) is an error —
          // never fall through to a success alert.
          if (validationResponse?.status === "Valid") {
            handleVisible(
              `FBR INVOICE# ${result?.fbrResponse?.invoiceNumber}`,
              "success",
              validationResponse,
            );
          } else if (validationResponse) {
            handleVisible("", "error", validationResponse);
          } else {
            handleVisible("", "error", {
              error:
                result?.message ||
                "Invoice was not posted to FBR. Please try again.",
            });
          }
        } else {
          const validCount = sendingInvoicesList?.validInvoicesCount || 0;
          const invalidCount = sendingInvoicesList?.invalidInvoicesCount || 0;
          const failedCount = sendingInvoicesList?.failedInvoicesCount || 0;
          if (validCount && !invalidCount && !failedCount) {
            handleVisible(res?.data?.message, "success", null);
          } else if (!validCount) {
            handleVisible("", "error", {
              error: `No invoice was posted to FBR. <br /> Invalid: ${invalidCount} <br /> Failed: ${failedCount}`,
            });
          } else {
            handleVisible(
              `${res?.data?.message} <br /> Total Valid Invoices: ${validCount} <br /> Total Invalid Invoices: ${invalidCount} <br /> Total Failed Invoices: ${failedCount}`,
              "success",
              null,
            );
          }
          setRowSelection({});
        }
        dispatch(
          getInvoices(
            page,
            limit,
            searchTerm,
            companyStatus,
            FBRInvoiceStatus,
            companyFilter,
          ),
        );
      })
      .catch((error) => {
        dispatch(sendInvoiceToFbrError(error?.response?.data?.message));
        handleDangerVisible(error?.response?.data?.message);
      });
  };
};

// Get Invoice Summary
export const getInvoiceSummaryRequest = () => {
  return {
    type: "INVOICE_SUMMARY_LIST_REQUEST",
  };
};
export const getInvoiceSummarySuccess = (data) => {
  return {
    type: "INVOICE_SUMMARY_LIST_SUCCESS",
    payload: data,
  };
};
export const getInvoiceSummaryError = (msg) => {
  return {
    type: "INVOICE_SUMMARY_LIST_ERROR",
    payload: msg,
  };
};
export const getInvoiceSummary = (data, setFormData, handleDangerVisible) => {
  return (dispatch) => {
    dispatch(getInvoiceSummaryRequest());
    API.post(`/invoice/invoice-summary`, data)
      .then((res) => {
        let invoiceSummaryList = res.data;
        dispatch(getInvoiceSummarySuccess(invoiceSummaryList));
        setFormData({
          startDate: "",
          endDate: "",
          companyId: "",
        });
      })
      .catch((error) => {
        dispatch(getInvoiceSummaryError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};

// Get Transection Rate with ID
export const getRateWithTransactionIdRequest = () => {
  return {
    type: "GET_RATE_WITH_TRANSACTION_ID_REQUEST",
  };
};
export const getRateWithTransactionIdSuccess = (data) => {
  return {
    type: "GET_RATE_WITH_TRANSACTION_ID_SUCCESS",
    payload: data,
  };
};
export const getRateWithTransactionIdError = (msg) => {
  return {
    type: "GET_RATE_WITH_TRANSACTION_ID_ERROR",
    payload: msg,
  };
};
export const getRateWithTransactionId = (id, handleDangerVisible) => {
  return (dispatch) => {
    dispatch(getRateWithTransactionIdRequest());
    API.get(`/invoice/get-rates-with-transactionId/${id}`)
      .then((res) => {
        let getRateWithTransactionIdList = res.data;
        dispatch(getRateWithTransactionIdSuccess(getRateWithTransactionIdList));
      })
      .catch((error) => {
        dispatch(getRateWithTransactionIdError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};

// Get SRO with Rate ID
export const getSroWithRateIdRequest = () => {
  return {
    type: "GET_SRO_WITH_RATE_ID_REQUEST",
  };
};
export const getSroWithRateIdSuccess = (data) => {
  return {
    type: "GET_SRO_WITH_RATE_ID_SUCCESS",
    payload: data,
  };
};
export const getSroWithRateIdError = (msg) => {
  return {
    type: "GET_SRO_WITH_RATE_ID_ERROR",
    payload: msg,
  };
};
export const getSroWithRateId = (id, handleDangerVisible) => {
  return (dispatch) => {
    dispatch(getSroWithRateIdRequest());
    API.get(`/invoice/get-SRO-with-rateId/${id}`)
      .then((res) => {
        let getSroWithRateIdList = res.data;
        dispatch(getSroWithRateIdSuccess(getSroWithRateIdList));
      })
      .catch((error) => {
        dispatch(getSroWithRateIdError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};

// Get SRO Item with SRO ID
export const getSroItemsWithSroIdRequest = () => {
  return {
    type: "GET_SRO_ITEMS_WITH_SRO_ID_REQUEST",
  };
};
export const getSroItemsWithSroIdSuccess = (data) => {
  return {
    type: "GET_SRO_ITEMS_WITH_SRO_ID_SUCCESS",
    payload: data,
  };
};
export const getSroItemsWithSroIdError = (msg) => {
  return {
    type: "GET_SRO_ITEMS_WITH_SRO_ID_ERROR",
    payload: msg,
  };
};
export const getSroItemsWithSroId = (id, handleDangerVisible) => {
  return (dispatch) => {
    dispatch(getSroItemsWithSroIdRequest());
    API.get(`/invoice/get-SROItems-with-SROId/${id}`)
      .then((res) => {
        let getSroItemsWithSroIdList = res.data;
        dispatch(getSroItemsWithSroIdSuccess(getSroItemsWithSroIdList));
      })
      .catch((error) => {
        dispatch(getSroItemsWithSroIdError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};

// Upload Bulk Inovice
export const uploadBulkInvoiceRequest = () => {
  return {
    type: "UPLOAD_BULK_INVOICE_REQUEST",
  };
};
export const uploadBulkInvoiceSuccess = (data) => {
  return {
    type: "UPLOAD_BULK_INVOICE_SUCCESS",
    payload: data,
  };
};
export const uploadBulkInvoiceError = (msg) => {
  return {
    type: "UPLOAD_BULK_INVOICE_ERROR",
    payload: msg,
  };
};
export const uploadBulkInvoice = (
  data,
  setFormData,
  handleVisible,
  handleDangerVisible,
) => {
  return (dispatch) => {
    dispatch(uploadBulkInvoiceRequest());
    API.post(`/invoice/upload-invoices`, data)
      .then((res) => {
        let uploadBulkInvoices = res.data;
        dispatch(uploadBulkInvoiceSuccess(uploadBulkInvoices));
        setFormData({
          companyId: "",
          file: null,
        });
        handleVisible(
          `Total Invoices Uploaded: ${uploadBulkInvoices?.totalInvoices}`,
        );
        dispatch(addingBulkInvoiceForMeCall(uploadBulkInvoices?.totalInvoices));
      })
      .catch((error) => {
        dispatch(uploadBulkInvoiceError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};

// Toggle Invoice Visibility
export const toggleInvoiceVisibilityRequest = () => ({
  type: "TOGGLE_INVOICE_VISIBILITY_REQUEST",
});
export const toggleInvoiceVisibilitySuccess = (data) => ({
  type: "TOGGLE_INVOICE_VISIBILITY_SUCCESS",
  payload: data,
});
export const toggleInvoiceVisibilityError = (msg) => ({
  type: "TOGGLE_INVOICE_VISIBILITY_ERROR",
  payload: msg,
});
export const toggleInvoiceVisibility = (
  id,
  handleVisible,
  handleDangerVisible,
  page,
  limit,
  searchTerm,
  companyStatus,
  FBRInvoiceStatus,
  companyFilter,
) => {
  return (dispatch) => {
    dispatch(toggleInvoiceVisibilityRequest());
    API.put(`/invoice/toggle-invoice-visibility/${id}`)
      .then((res) => {
        dispatch(toggleInvoiceVisibilitySuccess(res.data));
        handleVisible(res.data?.message);
        dispatch(
          getInvoices(
            page,
            limit,
            searchTerm,
            companyStatus,
            FBRInvoiceStatus,
            companyFilter,
          ),
        );
      })
      .catch((error) => {
        dispatch(toggleInvoiceVisibilityError(error?.response?.data?.message));
        handleDangerVisible(error?.response?.data?.message);
      });
  };
};

// Cancel Invoice Status
export const cancelInvoiceStatusRequest = () => {
  return {
    type: "CANCEL_INVOICE_STATUS_REQUEST",
  };
};
export const cancelInvoiceStatusSuccess = (data) => {
  return {
    type: "CANCEL_INVOICE_STATUS_SUCCESS",
    payload: data,
  };
};
export const cancelInvoiceStatusError = (msg) => {
  return {
    type: "CANCEL_INVOICE_STATUS_ERROR",
    payload: msg,
  };
};
export const cancelInvoiceStatus = (
  id,
  handleVisible,
  handleDangerVisible,
  page,
  limit,
  searchTerm,
  companyStatus,
  FBRInvoiceStatus,
  companyFilter,
) => {
  return (dispatch) => {
    dispatch(cancelInvoiceStatusRequest());
    API.post(`/invoice/update-invoice-status/${id}`, { status: "Cancel" })
      .then((res) => {
        dispatch(cancelInvoiceStatusSuccess(res.data));
        handleVisible(res.data?.message);
        dispatch(
          getInvoices(
            page,
            limit,
            searchTerm,
            companyStatus,
            FBRInvoiceStatus,
            companyFilter,
          ),
        );
      })
      .catch((error) => {
        dispatch(cancelInvoiceStatusError(error?.response?.data?.message));
        handleDangerVisible(error?.response?.data?.message);
      });
  };
};

// Get Invoice History
export const getInvoiceHistoryRequest = () => {
  return {
    type: "GET_INVOICE_HISTORY_REQUEST",
  };
};
export const getInvoiceHistorySuccess = (data) => {
  return {
    type: "GET_INVOICE_HISTORY_SUCCESS",
    payload: data,
  };
};
export const getInvoiceHistoryError = (msg) => {
  return {
    type: "GET_INVOICE_HISTORY_ERROR",
    payload: msg,
  };
};
export const getInvoiceHistory = (id, handleDangerVisible) => {
  return (dispatch) => {
    dispatch(getInvoiceHistoryRequest());
    API.get(`/invoice/invoice-history/${id}`)
      .then((res) => {
        dispatch(getInvoiceHistorySuccess(res.data));
      })
      .catch((error) => {
        dispatch(getInvoiceHistoryError(error?.response?.data?.message));
        handleDangerVisible(error?.response?.data?.message);
      });
  };
};

// Getting Transaction Type with Company ID
export const getTransactionWithCompanyIdRequest = () => {
  return {
    type: "TRANSACTION_LIST_WITH_COMPANY_ID_REQUEST",
  };
};
export const getTransactionWithCompanyIdSuccess = (data) => {
  return {
    type: "TRANSACTION_LIST_WITH_COMPANY_ID_SUCCESS",
    payload: data,
  };
};
export const getTransactionWithCompanyIdError = (msg) => {
  return {
    type: "TRANSACTION_LIST_WITH_COMPANY_ID_ERROR",
    payload: msg,
  };
};
export const getTransactionWithCompanyId = (
  id,
  setFormData,
  handleDangerVisible,
  callInCaseOfEdit = false,
) => {
  return (dispatch) => {
    dispatch(getTransactionWithCompanyIdRequest());
    API.get(`/scenario/get-assigned-scenarios/${id}`)
      .then((res) => {
        let transactionTypeWithCompanyId = res.data;
        dispatch(
          getTransactionWithCompanyIdSuccess(transactionTypeWithCompanyId),
        );
        if (!callInCaseOfEdit) {
          setFormData((prev) => ({
            ...prev,
            transactionType: "",
          }));
        }
      })
      .catch((error) => {
        dispatch(getTransactionWithCompanyIdError(apiErrorMessage(error)));
        handleDangerVisible(apiErrorMessage(error));
      });
  };
};
